import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSaveLink } from '../../../hooks/useSaveLink'
import { useAuth } from '../../../hooks/useAuth'

const TAG_SEPARATOR = ','
const SHARE_DRAFT_STORAGE_KEY = 'fts-share-draft'

interface ShareDraft {
  url: string
  title?: string
  note?: string
  tags?: string[]
}

const parseTags = (rawParams: URLSearchParams) => {
  const rawTagParams = rawParams.getAll('tag')
  if (rawTagParams.length) {
    return rawTagParams.map((tag) => tag.trim()).filter(Boolean)
  }

  const tagString = rawParams.get('tags')
  if (!tagString) {
    return []
  }

  return tagString
    .split(TAG_SEPARATOR)
    .map((tag) => tag.trim())
    .filter(Boolean)
}

const parseShareDraftFromSearchParams = (params: URLSearchParams): ShareDraft | null => {
  const url = params.get('url') ?? ''

  if (!url) {
    return null
  }

  return {
    url,
    title: params.get('title') ?? undefined,
    note: params.get('text') ?? params.get('note') ?? undefined,
    tags: parseTags(params),
  }
}

const parseShareDraftFromUrl = (input: string): ShareDraft | null => {
  const normalised = input.startsWith('webapp://') ? input.replace('webapp://', 'https://') : input

  try {
    const parsed = new URL(normalised)
    return parseShareDraftFromSearchParams(parsed.searchParams)
  } catch (error) {
    console.warn('Unable to parse share draft URL', error)
    return null
  }
}

function CaptureForm() {
  const [url, setUrl] = useState('')
  const [title, setTitle] = useState('')
  const [note, setNote] = useState('')
  const [tagsInput, setTagsInput] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const saveLink = useSaveLink()
  const { isAuthenticated, isReady, signInWithGoogle, requiresConfiguration, isAuthenticating } = useAuth()

  const tags = useMemo(
    () =>
      tagsInput
        .split(TAG_SEPARATOR)
        .map((tag) => tag.trim())
        .filter(Boolean),
    [tagsInput],
  )

  const incomingDraft = useMemo(() => parseShareDraftFromSearchParams(searchParams) ?? null, [searchParams])

  useEffect(() => {
    const hasIncomingParams = Boolean(incomingDraft)

    const applyDraft = (draft: ShareDraft) => {
      setUrl(draft.url)
      setTitle(draft.title ?? '')
      setNote(draft.note ?? '')
      setTagsInput(draft.tags?.length ? draft.tags.join(', ') : '')
    }

    if (!hasIncomingParams) {
      if (typeof window === 'undefined') {
        return
      }

      let restored = false

      const storedDraftRaw = window.localStorage.getItem(SHARE_DRAFT_STORAGE_KEY)
      if (storedDraftRaw) {
        try {
          const storedDraft = JSON.parse(storedDraftRaw) as ShareDraft

          if (storedDraft?.url) {
            applyDraft(storedDraft)
            restored = true
          }
        } catch (error) {
          console.warn('Unable to restore share draft', error)
        } finally {
          window.localStorage.removeItem(SHARE_DRAFT_STORAGE_KEY)
        }
      }

      if (!restored && typeof document !== 'undefined' && document.referrer) {
        const referrerDraft = parseShareDraftFromUrl(document.referrer)
        if (referrerDraft) {
          applyDraft(referrerDraft)
          restored = true
        }
      }

      if (!restored && typeof window !== 'undefined' && window.location.hash.includes('url=')) {
        const hash = window.location.hash.slice(1)
        const query = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : hash
        if (query) {
          const hashDraft = parseShareDraftFromSearchParams(new URLSearchParams(query))
          if (hashDraft) {
            applyDraft(hashDraft)
            restored = true
          }
        }
      }

      if (!restored) {
        return
      }

      return
    }

    if (!incomingDraft) {
      return
    }

    applyDraft(incomingDraft)

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SHARE_DRAFT_STORAGE_KEY, JSON.stringify(incomingDraft))
    }

    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      ;['url', 'title', 'text', 'tags', 'tag', 'source'].forEach((key) => next.delete(key))
      return next
    })
  }, [incomingDraft, setSearchParams])

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return
    }

    const handleMessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') {
        return
      }

      const { type, payload } = event.data as { type?: string; payload?: Record<string, unknown> }

      if (type !== 'share-target' && type !== 'SHARE_QUEUE_UPDATED') {
        return
      }

      const data = payload ?? (event.data.payload as Record<string, unknown> | undefined)

      if (!data) {
        return
      }

      if (typeof data.url === 'string') {
        setUrl(data.url)
      }
      if (typeof data.title === 'string') {
        setTitle(data.title)
      }
      if (typeof data.text === 'string') {
        setNote(data.text)
      }
    }

    navigator.serviceWorker.addEventListener('message', handleMessage)

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage)
    }
  }, [])

  const canSubmit = isAuthenticated && url.trim().length > 4 && !saveLink.isPending

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canSubmit) {
      return
    }

    saveLink.mutate(
      {
        url: url.trim(),
        title: title.trim() || undefined,
        note: note.trim() || undefined,
        tags: tags.length ? tags : undefined,
      },
      {
        onSuccess: () => {
          setUrl('')
          setTitle('')
          setNote('')
          setTagsInput('')
        },
      },
    )
  }

  return (
    <section style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 12px 32px -20px rgba(15,23,42,0.35)' }}>
      <header style={{ marginBottom: '16px' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '8px' }}>Quick save</h2>
        <p style={{ color: '#4f5b66' }}>
          Paste a link or trigger the Chrome extension to capture the current tab.
        </p>
      </header>

      {!isReady ? (
        <div style={{ padding: '16px', background: '#f1f5f9', borderRadius: '8px', color: '#475569', marginBottom: '16px' }}>
          Initialising authentication…
        </div>
      ) : null}

      {isReady && !isAuthenticated ? (
        <div
          style={{
            padding: '16px',
            borderRadius: '8px',
            background: '#fffbeb',
            border: '1px solid #facc15',
            color: '#854d0e',
            display: 'grid',
            gap: '12px',
            marginBottom: '16px',
          }}
        >
          <div>
            <p style={{ fontWeight: 600 }}>Sign in to save links</p>
            <p style={{ fontSize: '0.95rem' }}>
              Google sign-in keeps your Chrome extension, Android share target, and desktop dashboard in sync.
            </p>
          </div>
          <button
            type='button'
            onClick={signInWithGoogle}
            disabled={isAuthenticating}
            style={{
              justifySelf: 'start',
              padding: '10px 18px',
              borderRadius: '8px',
              border: 'none',
              background: requiresConfiguration ? '#0b1f33' : '#facc15',
              color: requiresConfiguration ? '#f8fafc' : '#1f2937',
              fontWeight: 700,
              cursor: isAuthenticating ? 'not-allowed' : 'pointer',
            }}
          >
            {isAuthenticating ? 'Connecting…' : requiresConfiguration ? 'Use demo account' : 'Sign in with Google'}
          </button>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '16px' }}>
        <div style={{ display: 'grid', gap: '8px' }}>
          <label htmlFor='capture-url' style={{ fontWeight: 600 }}>
            URL
          </label>
          <input
            id='capture-url'
            type='url'
            placeholder='https://example.com/article'
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            required
            disabled={!isAuthenticated}
            style={{
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid #d0d7de',
              background: isAuthenticated ? 'white' : '#e2e8f0',
            }}
          />
        </div>

        <div style={{ display: 'grid', gap: '8px' }}>
          <label htmlFor='capture-title' style={{ fontWeight: 600 }}>
            Title (optional)
          </label>
          <input
            id='capture-title'
            type='text'
            placeholder='Auto-detected if left blank'
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            disabled={!isAuthenticated}
            style={{
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid #d0d7de',
              background: isAuthenticated ? 'white' : '#e2e8f0',
            }}
          />
        </div>

        <div style={{ display: 'grid', gap: '8px' }}>
          <label htmlFor='capture-note' style={{ fontWeight: 600 }}>
            Personal note (optional)
          </label>
          <textarea
            id='capture-note'
            rows={3}
            placeholder='Why is this link important?'
            value={note}
            onChange={(event) => setNote(event.target.value)}
            disabled={!isAuthenticated}
            style={{
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid #d0d7de',
              resize: 'vertical',
              background: isAuthenticated ? 'white' : '#e2e8f0',
            }}
          />
        </div>

        <div style={{ display: 'grid', gap: '8px' }}>
          <label htmlFor='capture-tags' style={{ fontWeight: 600 }}>
            Tags (comma separated)
          </label>
          <input
            id='capture-tags'
            type='text'
            placeholder='ai, research, read-later'
            value={tagsInput}
            onChange={(event) => setTagsInput(event.target.value)}
            disabled={!isAuthenticated}
            style={{
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid #d0d7de',
              background: isAuthenticated ? 'white' : '#e2e8f0',
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <button
            type='submit'
            disabled={!canSubmit}
            style={{
              padding: '12px 20px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: canSubmit ? '#0b1f33' : '#9aa5b1',
              color: 'white',
              fontWeight: 600,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {saveLink.isPending ? 'Saving…' : isAuthenticated ? 'Save link' : 'Sign in to save'}
          </button>
          {saveLink.isSuccess && !saveLink.isPending ? (
            <span style={{ color: '#1b7f5f', fontWeight: 600 }}>Saved! Processing will continue in the background.</span>
          ) : null}
          {saveLink.isError ? (
            <span style={{ color: '#c0353b', fontWeight: 600 }}>
              {saveLink.error instanceof Error ? saveLink.error.message : 'Unable to save link'}
            </span>
          ) : null}
        </div>
      </form>
    </section>
  )
}

export default CaptureForm
